<template>
  <Pulse v-if="loading" />
  <b-overlay v-else :show="saving">
    <div>
      <b-row>
        <!-- Current Subscription Card -->
        <b-col md="6" class="mt-4">
          <b-card header="Current Subscription">
            <div v-if="subscription">
              <div class="mb-3">
                <strong>Plan:</strong> {{ subscription.planName }}
              </div>
              <div class="mb-3">
                <strong>Status:</strong>
                <b-badge
                  :variant="getStatusVariant(subscription.status)"
                  class="ml-2"
                >
                  {{ subscription.status }}
                </b-badge>
              </div>
              <div class="mb-3" v-if="subscription.nextBillingDate">
                <strong>Next Billing Date:</strong>
                {{ formatDate(subscription.nextBillingDate) }}
              </div>
              <div class="mb-3" v-if="subscription.amount">
                <strong>Amount:</strong> £{{ subscription.amount }} /
                {{ subscription.interval }}
              </div>
              <b-button
                class="ovr-button"
                variant="danger"
                @click="showCancelModal"
                :disabled="
                  subscription.status === 'cancelled' ||
                  !subscription.cancellable || isAppSubscription
                "
              >
                Cancel Subscription
              </b-button>
            </div>
            <div v-else>
              <p class="text-muted">No active subscription found.</p>
            </div>
          </b-card>

          <!-- Card Management Card -->
          <b-card header="Payment Method" class="mt-4">
            <p>Manage your payment details through your payment provider.</p>
            <div v-if="subscription && subscription.planName" class="mb-3">
              <div class="mb-2">
                <strong>Payment Provider:</strong> {{ subscription.planName }}
              </div>
              <p class="text-muted">
                <small
                  >Your card details are securely stored with your payment
                  provider.</small
                >
              </p>
            </div>
            <b-button
              class="ovr-button"
              variant="primary"
              @click="managePaymentMethod"
              :disabled="
                !subscription ||
                subscription.status !== 'active' ||
                !isKartra
              "
            >
              Manage Payment Details
            </b-button>
            <p
              class="text-muted mt-3"
              v-if="subscription && subscription.active"
            >
              <small
                >You'll be redirected to your payment provider's secure
                portal.</small
              >
            </p>
          </b-card>
        </b-col>

        <!-- Update Subscription Card -->
        <b-col md="6" class="mt-4">
          <b-card header="Change Subscription Plan">
            <p>
              Select a subscription plan to upgrade or change your membership.
            </p>
            <div class="mb-3">
              <strong>Current Plan:</strong>
              {{ subscription ? subscription.planName : "Free" }}
            </div>
            <b-row>
              <b-col cols="6">
                <b-button
                  class="ovr-button w-100"
                  variant="info"
                  @click="handleUpgrade('monthly')"
                  :disabled="!canUpgrade('monthly')"
                 
                >
                  <div class="text-left">
                    <div>
                      <strong>Monthly</strong>
                      <span
                        v-if="subscription?.interval && subscription.interval.toLowerCase() === 'monthly'"
                        class="small ml-1"
                        >(Current)</span
                      >
                    </div>
                    <div class="mt-1">£2.99/month</div>
                  </div>
                </b-button>
              </b-col>
              <b-col cols="6">
                <b-button
                  class="ovr-button w-100"
                  variant="info"
                  @click="handleUpgrade('yearly')"
                  :disabled="!canUpgrade('yearly')"
                
                >
                  <div class="text-left">
                    <div>
                      <strong>Yearly</strong>
                      <span
                        v-if="subscription?.interval && subscription.interval.toLowerCase() === 'yearly'"
                        class="small ml-1"
                        >(Current)</span
                      >
                    </div>
                    <div class="mt-1">£24.99/year</div>
                  </div>
                </b-button>
              </b-col>
            </b-row>
            <p class="text-muted mt-3" v-if="(isGoCardless || isKartra) && subscription?.status === 'active'">
              <small
                >Your new plan will take effect when your current subscription ends on {{ formatDate(subscription?.nextBillingDate) }}.</small
              >
            </p>
            <p class="text-muted mt-3" v-else-if="subscription?.status === 'active'">
              <small
                >Note: To change plans, you'll need to cancel your current
                subscription and purchase a new one.</small
              >
            </p>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Cancel Subscription Modal -->
    <b-modal
      ref="cancelModal"
      title="Cancel Subscription"
      @ok="cancelSubscription"
      ok-variant="danger"
      ok-title="Yes, Cancel Subscription"
      cancel-title="Keep Subscription"
    >
      <p>Are you sure you want to cancel your subscription?</p>
      <p class="text-muted">
        <small
          >Your subscription will remain active until the end of the current
          billing period ({{
            formatDate(subscription?.nextBillingDate)
          }}).</small
        >
      </p>
    </b-modal>
  </b-overlay>
</template>

<script>
import Pulse from "@/components/loaders/Pulse";

export default {
  name: "PaymentManagement",
  components: { Pulse },
  data() {
    return {
      loading: true,
      saving: false,
      subscription: null,
      // Full membership payload from v2/profile/membership so we can branch on provider
      membership: null,
    };
  },
  computed: {
    paymentProvider() {
      return this.membership?.membershipSource || null;
    },
    isGoCardless() {
      return this.paymentProvider === "Direct Debit - GoCardless";
    },
    isKartra() {
      return this.paymentProvider === "PayPal / Credit Card";
    },
    isPayPal() {
      return this.paymentProvider === "PayPal";
    },
    isAppSubscription() {
      return this.paymentProvider === "In-app Subscription";
    },
    isComplimentary() {
      return this.paymentProvider === "Complimentary";
    },
    isOrganisationMembership() {
      // Organisation memberships are active, non-cancellable and use the organisation name
      return !!(
        this.membership &&
        this.membership.active &&
        !this.membership.cancellable &&
        !["Free", "Complimentary", "Direct Debit - GoCardless", "PayPal / Credit Card", "PayPal", "In-app Subscription"].includes(this.paymentProvider)
      );
    },
  },
  methods: {
    // Open Kartra checkout in new tab
    async openKartraPopup(ktValue) {
      const owner = process.env.VUE_APP_KARTRA_OWNER;
      
      if (!ktValue || !owner) {
        console.warn("Kartra configuration missing");
        return false;
      }

      try {
        const url = `https://traumacare.kartra.com/checkout/${ktValue}`;
        window.open(url, '_blank');
        return true;
      } catch (err) {
        console.error("Failed to open Kartra link", err);
        return false;
      }
    },

    async loadSubscriptionData() {
      this.loading = true;
      try {
        const membershipResponse = await this.$api.v2.get("profile/membership");
        const membershipData = membershipResponse.body;

        this.membership = membershipData;

        if (membershipData.active) {
          this.subscription = {
            planName: membershipData.membershipSource,
            status: membershipData.active ? "active" : "cancelled",
            nextBillingDate: membershipData.endDate,
            amount: null, // Not provided by API
            interval: membershipData.planInterval || null,
            autoRenew: membershipData.autoRenew,
            cancellable: membershipData.cancellable,
          };
        } else {
          this.subscription = null;
        }
      } catch (error) {
        this.showErrorToast("Failed to load payment information");
        console.error("Error loading payment data:", error);
      } finally {
        this.loading = false;
      }
    },

    showCancelModal() {
      this.$refs.cancelModal.show();
    },

    async cancelSubscription() {
      this.saving = true;
      try {
        await this.$api.v2.post("profile/membership/cancel");
        this.showSuccessToast("Subscription cancelled successfully");
        location.reload();
      } catch (error) {
        this.showErrorToast("Failed to cancel subscription");
        console.error("Error cancelling subscription:", error);
      } finally {
        this.saving = false;
      }
    },

    async handleUpgrade(planType) {
      // 1. Active & Auto-Renewing Subscriptions (Modifications)
      if (this.subscription?.status === "active" && this.subscription?.autoRenew) {
        if (this.isOrganisationMembership || this.isComplimentary || this.isPayPal || this.isAppSubscription) {
          this.showSupportMessage();
          return;
        }

        // Use API for supported providers (Kartra / GoCardless)
        if (this.isKartra || (this.isGoCardless && this.subscription.interval.toLowerCase() !== planType)) {
          await this.changePlan(planType);
          return;
        }
        
        // GoCardless same-plan case already blocked by UI
        if (this.isGoCardless) return;
      }

      // 2. New / Inactive Subscriptions (Purchases)
      // Block restricted providers, BUT allow inactive App Subscriptions to switch to web
      if (this.isOrganisationMembership || this.isComplimentary || this.isPayPal) {
         this.showSupportMessage();
         return;
      }

      // Kartra Users -> Open Kartra Popup
      if (this.isKartra) {
        const kt = planType === "monthly" 
          ? process.env.VUE_APP_KARTRA_MONTHLY_KT
          : process.env.VUE_APP_KARTRA_YEARLY_KT;
        if (await this.openKartraPopup(kt)) return;
      }

      // Default (GoCardless) -> Open Checkout
      const checkoutUrl = planType === "monthly"
        ? "https://pay.gocardless.com/AL0009FCS98227"
        : "https://pay.gocardless.com/AL0009FCR4M3J0";
      window.open(checkoutUrl, "_blank");
    },

    async changePlan(planType) {
      this.saving = true;
      try {
        const response = await this.$api.v2.post("profile/membership/change-plan", {
          plan_type: planType,
        });
        this.showSuccessToast(response.body.message || "Plan changed successfully");
        location.reload();
      } catch (error) {
        const message = error.body?.message || "Failed to change plan";
        this.showErrorToast(message);
        console.error("Error changing plan:", error);
      } finally {
        this.saving = false;
      }
    },



    async managePaymentMethod() {
      if (this.subscription && this.subscription.status === "active" && this.isKartra) {


        try {
          // Call our own backend to handle the Kartra authentication and get the redirect URL
          const response = await this.$api.v2.post('profile/membership/portal-link');

          const redirectUrl = response.body.redirect_url;

          if (redirectUrl) {
            console.log("Kartra link retrieved, redirecting to:", redirectUrl);
            window.open(redirectUrl, '_blank');
          } else {
            console.error("No redirect URL returned from backend");
            this.showErrorToast("Could not access billing portal. Please contact support.");
          }
        } catch (error) {
          console.error("Error getting Kartra portal link:", error);
          this.showErrorToast("Failed to load billing portal. Please try again later.");
        }

        return;
      }
      this.showSupportMessage();
    },

 
     showSupportMessage() {
      let message =
        "Your membership is managed outside of CPDme. Please contact support to change your plan or payment method.";

      if (this.isOrganisationMembership) {
        message =
          "Your membership is managed through your organisation. Please contact your organisation administrator or CPDme support to make changes.";
      } else if (this.isComplimentary) {
        message =
          "You are on a complimentary/custom membership. Please contact CPDme support to make any changes to your plan.";
      } else if (this.isPayPal) {
        message =
          "Your membership is billed via PayPal. Please contact support to change your plan or payment method.";
      } else if (this.isAppSubscription) {
        message = 
          "Your subscription is managed through the app store.";
      }

      this.showErrorToast(message);
    },

    formatDate(date) {
      if (!date) return "N/A";
      return this.moment(date).format("DD/MM/YYYY");
    },

    getStatusVariant(status) {
      const variants = {
        active: "success",
        cancelled: "danger",
        past_due: "warning",
        trialing: "info",
        paid: "success",
        pending: "warning",
        failed: "danger",
      };
      return variants[status?.toLowerCase()] || "secondary";
    },

    showSuccessToast(message) {
      this.$bvToast.toast(message, {
        title: "Success",
        variant: "success",
        solid: true,
      });
    },

    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: "Error",
        variant: "danger",
        solid: true,
      });
    },

    canUpgrade(targetPlan) {
      // 1. App Subscriptions cannot upgrade via web IF they are active
      if (this.isAppSubscription && this.subscription?.status === 'active') {
        return false;
      }

      // 2. Organisation Memberships cannot upgrade (must ask admin)
      if (this.isOrganisationMembership) {
        return false;
      }

      // 3. Active & Auto-Renewing users cannot "upgrade" to the SAME plan they already have
      if (this.subscription?.status === 'active' && this.subscription?.autoRenew) {
        const currentInterval = (this.subscription.interval || '').toLowerCase();
        if (currentInterval === targetPlan) {
          return false;
        }
      }

      // Default: Allowed
      return true;
    },
  },
  created() {
    this.loadSubscriptionData();
  },
};
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
